import React, { useState, useContext } from 'react'
import moment from 'moment'
import startOfToday from 'date-fns/startOfToday'
import { Form, Button, Select, DatePicker, Checkbox, InputNumber } from 'antd'

import { ErpClientContext } from '../../../services/erp'
import { OffersContext } from '../context'
import { ProductSelector } from '../../../components/product-selector'
import { toISOStringWithTimezone } from '../../../utils'

import 'antd/dist/antd.css'
import './styles.css'

const initial = {
  id_tipo: 'o',
  caducable: false,
  products: [],
}

// Función auxiliar para formatear la fecha con milisegundos a 0
function formatDateWithZeroMilliseconds(date) {
  return moment(date).milliseconds(0).format('YYYY-MM-DDTHH:mm:ss.000')
}

export function OffersForm({ onClose }) {
  const [form] = Form.useForm()
  const erpService = useContext(ErpClientContext)
  const ctx = useContext(OffersContext)
  const [status, setStatus] = useState({})
  const [state, setState] = useState(initial)

  async function onFinish() {
    setStatus({ type: 'creating' })
    const offers = state.products.map((p) => ({
      ...state,
      descuento_especial:
        typeof state.descuento_especial === 'number'
          ? state.descuento_especial / 100
          : undefined,
      descuento_oferta:
        typeof state.descuento_oferta === 'number'
          ? state.descuento_oferta / 100
          : undefined,
      id_producto: p,
      products: undefined,
    }))
    const ok = await erpService.createOffers(offers).catch((e) => {
      if (e.message.includes('overlapping_date_exists')) {
        setStatus({
          type: 'error',
          message: 'There is an existing offer overlapping the same date',
        })
        return
      }
      setStatus({
        type: 'error',
        message: 'There is an error creating the offer',
      })
    })

    if (ok) {
      ctx.onUpdate()
      onClose()
    }
  }

  return (
    <>
      <Form
        form={form}
        className="offers-form"
        initialValues={initial}
        onFinish={onFinish}
        onValuesChange={(c, fields) => {
          const [fecha_desde, fecha_hasta] = fields.duration
            ? [
                formatDateWithZeroMilliseconds(fields.duration[0]),
                formatDateWithZeroMilliseconds(fields.duration[1]),
              ]
            : [undefined, undefined]

          setState((s) => ({
            ...s,
            ...fields,
            fecha_desde,
            fecha_hasta,
            duration: undefined,
          }))
        }}
      >
        <Form.Item label="Type" name="id_tipo">
          <Select
            style={{ width: 120 }}
            value={state.id_tipo}
            defaultValue={state.id_tipo}
          >
            <Select.Option value="o">Offer</Select.Option>
            <Select.Option value="p">Promotion</Select.Option>
            <Select.Option value="e">Special Price</Select.Option>
            <Select.Option value="m">Mix 6</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          label="Product"
          name="products"
          rules={[{ required: true, message: 'Please input the product' }]}
        >
          <ProductSelector
            id="offer-product"
            type="refs"
            placeholder="Search products to appy the offer"
            products={state.products}
          />
        </Form.Item>

        <Form.Item
          label="Duration"
          name="duration"
          rules={[
            { required: true, message: 'Please input the duration' },
            () => ({
              validator(rule, value) {
                if (
                  !value ||
                  new Date(value[0].toISOString()).getTime() >=
                    startOfToday().getTime()
                ) {
                  return Promise.resolve()
                }
                return Promise.reject(`The offer shouldn't be expired`)
              },
            }),
          ]}
        >
          <DatePicker.RangePicker
            showTime={{
              defaultValue: [
                moment('00:00:00', 'HH:mm:ss'),
                moment('23:59:59', 'HH:mm:ss'),
              ],
            }}
          />
        </Form.Item>

        {state.id_tipo === 'o' && (
          <Form.Item
            label="Discount offer(%)"
            name="descuento_oferta"
            rules={[
              { required: true, message: 'Please input the discount offer' },
            ]}
          >
            <InputNumber min={0} max={100} step={1} />
          </Form.Item>
        )}

        {state.id_tipo === 'p' && (
          <>
            <Form.Item
              label="Uni compra"
              name="uni_compra"
              rules={[
                { required: true, message: 'Please input the uni compra' },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item
              label="Uni promo"
              name="uni_promo"
              rules={[
                { required: true, message: 'Please input the unit promo' },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
          </>
        )}

        {state.id_tipo === 'e' && (
          <>
            <Form.Item
              label="Quantity of special price"
              name="cantidad_precio_especial"
              rules={[
                {
                  required: true,
                  message: 'Please input the quantity of special price',
                },
              ]}
            >
              <InputNumber min={0} />
            </Form.Item>
            <Form.Item
              label="Special discount(%)"
              name="descuento_especial"
              rules={[
                {
                  required: true,
                  message: 'Please input the special discount',
                },
              ]}
            >
              <InputNumber min={0} max={100} step={1} />
            </Form.Item>
          </>
        )}

        <Form.Item label="Expirable" name="caducable" valuePropName="checked">
          <Checkbox />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            disabled={status.type === 'creating'}
          >
            Create
          </Button>
        </Form.Item>

        {status.type === 'error' && (
          <div style={{ color: 'red' }}>{status.message}</div>
        )}
      </Form>
    </>
  )
}
